import { FccWaypointState } from '@backoffice-frontend/graphql'
import { usePrevious } from '@backoffice-frontend/hooks'
import { hasNoActiveMission } from '@backoffice-frontend/vehicle-common'
import { Card } from '@moia-dev/pace-web'
import { AnimatePresence, motion } from 'framer-motion'
import size from 'lodash/size'
import sum from 'lodash/sum'
import { useState } from 'react'
import {
  AD_VEHICLE_MISSION_WAYPOINT_ACTION_STATE_HEIGHT,
  DEFAULT_TRANSITION,
  TRANSITION_DURATION_IN_SEC,
  VEHICLE_MISSION_WAYPOINT_ACTION_HEIGHT,
  VEHICLE_MISSION_WAYPOINT_ACTION_MARGIN_HEIGHT,
  VEHICLE_MISSION_WAYPOINT_DESTINATION_DIVIDER_HEIGHT,
  VEHICLE_MISSION_WAYPOINT_DESTINATION_HEIGHT,
  VEHICLE_MISSION_WAYPOINT_DIVIDER_HEIGHT,
  VEHICLE_MISSION_WAYPOINT_EXPANDER_HEIGHT,
  VEHICLE_MISSION_WAYPOINT_HEADER_HEIGHT,
  VEHICLE_MISSION_WAYPOINT_MARGIN_BOTTOM_HEIGHT,
} from './constants'
import { VehicleMissionCardExpanderButton } from './VehicleMissionCardExpanderButton/VehicleMissionCardExpanderButton'
import {
  VEHICLE_MISSION_CARD_FOOTER_HEIGHT,
  VehicleMissionCardFooter,
} from './VehicleMissionCardFooter/VehicleMissionCardFooter'
import {
  VEHICLE_MISSION_CARD_HEADER_HEIGHT,
  VehicleMissionCardHeader,
} from './VehicleMissionCardHeader/VehicleMissionCardHeader'
import {
  getActionsStatus,
  isTripAction,
} from './VehicleMissionWaypoint/actionStatus'
import { VehicleMissionWaypoint } from './VehicleMissionWaypoint/VehicleMissionWaypoint'
import type { VehicleMissionCardVehicleFragment } from './vehicleMissionCard.hook'

type VehicleMissionCardProps = {
  className?: string
  vehicle: VehicleMissionCardVehicleFragment
}

const INITIALLY_VISIBLE_WAYPOINTS = 2

export const VehicleMissionCard = ({
  className,
  vehicle,
}: VehicleMissionCardProps) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const vehicleHasNoActiveMission = hasNoActiveMission(vehicle)

  const footerWaypoint = vehicle.fccUpcomingWaypoints?.find(wp =>
    wp.actions.find(a => a.__typename === 'FccWaypointHubReturnAction'),
  )
  const listOfWaypoints =
    vehicle.fccUpcomingWaypoints?.filter(wp => wp.id !== footerWaypoint?.id) ??
    []

  const isExpandable = listOfWaypoints.length > INITIALLY_VISIBLE_WAYPOINTS
  const amountOfOutOfViewportWaypoints = listOfWaypoints.slice(
    INITIALLY_VISIBLE_WAYPOINTS,
  ).length

  const visibleWaypointHeights = listOfWaypoints
    .slice(0, isExpanded ? undefined : INITIALLY_VISIBLE_WAYPOINTS)
    .map((waypoint, index) => {
      const tripActions = waypoint.actions.filter(isTripAction)
      const actionsStatus = getActionsStatus(waypoint)
      let adActionStatesHeight = 0

      if (waypoint.adState === FccWaypointState.Reached) {
        adActionStatesHeight =
          size(actionsStatus) * AD_VEHICLE_MISSION_WAYPOINT_ACTION_STATE_HEIGHT
      }

      const actionsHeight =
        tripActions.length *
        (VEHICLE_MISSION_WAYPOINT_ACTION_HEIGHT +
          VEHICLE_MISSION_WAYPOINT_ACTION_MARGIN_HEIGHT)

      return (
        VEHICLE_MISSION_WAYPOINT_HEADER_HEIGHT +
        VEHICLE_MISSION_WAYPOINT_DESTINATION_HEIGHT +
        (actionsHeight
          ? VEHICLE_MISSION_WAYPOINT_DESTINATION_DIVIDER_HEIGHT + actionsHeight
          : 0) +
        adActionStatesHeight +
        VEHICLE_MISSION_WAYPOINT_MARGIN_BOTTOM_HEIGHT +
        (index < listOfWaypoints?.length - 1
          ? VEHICLE_MISSION_WAYPOINT_DIVIDER_HEIGHT
          : 0)
      )
    })
  const sumVisibleWaypointHeights = sum(visibleWaypointHeights)
  const sumPreviouslyVisibleWaypointHeights = usePrevious(
    sumVisibleWaypointHeights,
  )
  const sumRemovedWaypointsHeights =
    sumVisibleWaypointHeights - (sumPreviouslyVisibleWaypointHeights ?? 0)

  const vehicleMissionCardHeight = vehicleHasNoActiveMission
    ? VEHICLE_MISSION_CARD_HEADER_HEIGHT +
      VEHICLE_MISSION_CARD_FOOTER_HEIGHT +
      (isExpandable ? VEHICLE_MISSION_WAYPOINT_EXPANDER_HEIGHT : 0)
    : VEHICLE_MISSION_CARD_HEADER_HEIGHT +
      sumVisibleWaypointHeights +
      (VEHICLE_MISSION_CARD_FOOTER_HEIGHT +
        (isExpandable ? VEHICLE_MISSION_WAYPOINT_EXPANDER_HEIGHT : 0)) +
      2 // 2 * 1px border

  return (
    <Card
      outline
      padding="none"
      cornerRadius="medium"
      className={className}
      css={{
        // Min and max height are needed for animating expanding and collapsing the card
        minHeight: vehicleMissionCardHeight,
        maxHeight: vehicleMissionCardHeight,
        transition: `max-height ${TRANSITION_DURATION_IN_SEC}s ease-out, min-height ${TRANSITION_DURATION_IN_SEC}s ease-out`,
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      <VehicleMissionCardHeader vehicle={vehicle} />
      <motion.div layout layoutRoot>
        <AnimatePresence initial={false} mode="popLayout">
          {listOfWaypoints?.map((waypoint, index) => (
            <motion.div
              key={waypoint.actions.at(0)?.id}
              css={{ position: 'relative', zIndex: 2 }}
              variants={{
                initial: {
                  x: -560, // ~ card width in drawer
                  transition: {
                    ...DEFAULT_TRANSITION,
                    delay: TRANSITION_DURATION_IN_SEC,
                  },
                },
                animate: { x: 0, y: 0 },
                exit: {
                  x: 0,
                  y: -sumRemovedWaypointsHeights,
                  zIndex: 1,
                },
              }}
              initial="initial"
              animate="animate"
              exit="exit"
              layout="position"
              viewport={{ once: true }}
              transition={{
                default: DEFAULT_TRANSITION,
              }}
            >
              <VehicleMissionWaypoint
                waypoint={waypoint}
                isCurrent={index === 0}
              />
            </motion.div>
          ))}
        </AnimatePresence>
      </motion.div>

      {isExpandable && (
        <VehicleMissionCardExpanderButton
          setIsExpanded={setIsExpanded}
          isExpanded={isExpanded}
          amountOfOutOfViewportWaypoints={amountOfOutOfViewportWaypoints}
        />
      )}

      <VehicleMissionCardFooter
        cardHasListOfMultipleWaypoints={listOfWaypoints.length > 0}
        waypoint={footerWaypoint}
      />
    </Card>
  )
}
